import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, LogOut, Check, Mic, MicOff, PhoneOff, Headphones, PictureInPicture2, Sparkles, Share2, Clapperboard, Users, MessageCircle, MoreHorizontal, X, Tv, Search, Maximize, ArrowLeft, Flag, Bell } from "lucide-react";
import { getUser, saveUser } from "@/lib/session";
import { useAuth } from "@/lib/auth";
import { BrandLogo } from "@/components/BrandLogo";
import ChatPanel from "@/components/ChatPanel";
import PersonalityPicker from "@/components/PersonalityPicker";
import BrowseTogether from "@/components/BrowseTogether";
import IPTVPlayer from "@/components/IPTVPlayer";
import ReportAbuseModal from "@/components/ReportAbuseModal";
import LiveTVPanel from "@/components/LiveTVPanel";
import { clearCaptionCache } from "@/components/SubtitleOverlay";
import { extractVideoId } from "@/components/VideoPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function wsUrl(code) {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/${code}`;
}

function Tile({ testId, onClick, icon, label, active }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors active:scale-95 ${active ? "bg-[#00D9FF]/20 border-[#00D9FF]/50 text-white" : "bg-white/5 border-white/10 text-[#E4E4E7]"}`}
    >
      <span className="w-9 h-9 rounded-full bg-black/30 flex items-center justify-center">{icon}</span>
      <span className="text-[11px] font-medium">{label}</span>
    </button>
  );
}

function NotifToggle({ testId, label, checked, onChange }) {
  return (
    <button data-testid={testId} onClick={() => onChange(!checked)} className="w-full flex items-center justify-between gap-3 py-2">
      <span className="text-sm text-[#E4E4E7]">{label}</span>
      <span className={`w-9 h-5 rounded-full transition-colors relative shrink-0 ${checked ? "bg-[#00D9FF]" : "bg-white/15"}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}

export default function WatchRoom() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { account, loading: authLoading, enterRoom, leaveRoom } = useAuth();
  const [searchParams] = useSearchParams();
  const rewatchUrlRef = useRef(searchParams.get("v"));
  const rewatchSentRef = useRef(false);
  const [me, setMe] = useState(() => getUser());
  const [viewers, setViewers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const countdownTimerRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [floats, setFloats] = useState([]);
  const [personality, setPersonality] = useState("hype");
  const [typingUsers, setTypingUsers] = useState([]);
  const [myId, setMyId] = useState(null);
  const [browseUrl, setBrowseUrl] = useState(null);
  const [iptvChannel, setIptvChannel] = useState(null);
  const [tvPanelOpen, setTvPanelOpen] = useState(false);
  const [autoM3u, setAutoM3u] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [controlRequest, setControlRequest] = useState(null);
  const [inVoice, setInVoice] = useState(false);
  const [muted, setMuted] = useState(false);
  const [voiceIds, setVoiceIds] = useState(() => new Set());
  const [remoteStreams, setRemoteStreams] = useState({});
  const [captionSignal, setCaptionSignal] = useState(0);
  const [syncStatus, setSyncStatus] = useState(null); // 'synced' | 'behind' | null
  const [upNext, setUpNext] = useState([]);
  const [pickerActive, setPickerActive] = useState(null);
  const [barsVisible, setBarsVisible] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportPrefill, setReportPrefill] = useState("");
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [pins, setPins] = useState([]);
  const [reads, setReads] = useState([]);
  const [notifPrefs, setNotifPrefs] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("ibeflix_notif")); return s || { sound: true, visual: true, badge: true }; }
    catch { return { sound: true, visual: true, badge: true }; }
  });
  const [notifOpen, setNotifOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const openSearchRef = useRef(null);
  const rootRef = useRef(null);
  const hasMediaRef = useRef(false);

  const wsRef = useRef(null);
  const playerRef = useRef(null);
  const unmountedRef = useRef(false);
  const retryRef = useRef(null);
  const attemptsRef = useRef(0);
  const typingTimers = useRef({});
  const peersRef = useRef({});
  const localStreamRef = useRef(null);
  const voiceApiRef = useRef({});
  const isDriverRef = useRef(false);
  const audioCtxRef = useRef(null);
  const meRef = useRef(me);
  const notifPrefsRef = useRef(notifPrefs);
  const mobileChatOpenRef = useRef(mobileChatOpen);
  const chatAtBottomRef = useRef(true);
  const chatScrollToBottomRef = useRef(null);

  const burst = useCallback((emoji) => {
    const id = Math.random().toString(36).slice(2);
    const left = 20 + Math.random() * 60;
    setFloats((f) => [...f, { id, emoji, left }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 2600);
  }, []);

  const reactionWinRef = useRef({});
  const registerReaction = useCallback((emoji) => {
    if (!emoji) return;
    const now = Date.now();
    const win = reactionWinRef.current;
    const arr = (win[emoji] || []).filter((t) => now - t < 3000);
    arr.push(now);
    win[emoji] = arr;
    if (arr.length >= 3) {
      // emoji shower — 3+ of the same emoji within 3s
      for (let i = 0; i < 6; i += 1) setTimeout(() => burst(emoji), i * 80);
    } else {
      burst(emoji);
    }
  }, [burst]);

  const playChime = useCallback((kind) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine";
      const start = kind === "leave" ? 520 : 660;
      const end = kind === "leave" ? 392 : 880;
      o.frequency.setValueAtTime(start, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(end, ctx.currentTime + 0.14);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.1, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.34);
      o.start();
      o.stop(ctx.currentTime + 0.36);
    } catch (e) { /* chime unavailable */ }
  }, []);

  const messageChime = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.setValueAtTime(880, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.08);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      o.start();
      o.stop(ctx.currentTime + 0.27);
    } catch (e) { /* chime unavailable */ }
  }, []);

  const openChatAndScroll = useCallback(() => {
    setMobileChatOpen(true);
    setUnread(0);
    setTimeout(() => chatScrollToBottomRef.current?.(), 120);
  }, []);

  const notifyNewMessage = useCallback((m) => {
    const prefs = notifPrefsRef.current;
    if (!m || m.name === meRef.current?.name) return;
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    const chatVisible = isDesktop
      ? (document.hasFocus() && chatAtBottomRef.current)
      : mobileChatOpenRef.current;
    if (chatVisible) return;
    if (prefs.sound && document.hasFocus()) messageChime();
    if (prefs.visual) {
      const preview = (m.text || "").slice(0, 60);
      const who = m.isHost ? "🎬 IBEFLIX Host" : `${m.flag || ""} ${m.name}`.trim();
      toast(`💬 ${who}`, { description: preview, action: { label: "View", onClick: openChatAndScroll } });
    }
    if (prefs.badge) setUnread((n) => n + 1);
  }, [messageChime, openChatAndScroll]);

  useEffect(() => { meRef.current = me; }, [me]);
  useEffect(() => {
    notifPrefsRef.current = notifPrefs;
    try { localStorage.setItem("ibeflix_notif", JSON.stringify(notifPrefs)); } catch (e) { /* storage full */ }
  }, [notifPrefs]);
  useEffect(() => {
    mobileChatOpenRef.current = mobileChatOpen;
    if (mobileChatOpen) setUnread(0);
  }, [mobileChatOpen]);
  const setPref = (key, val) => setNotifPrefs((p) => ({ ...p, [key]: val }));

  useEffect(() => {
    if (me) return;
    if (authLoading) return;
    if (account) {
      const derived = {
        name: account.username,
        country: account.country,
        flag: account.flag,
        language: account.language,
        countryCode: account.countryCode,
        avatarColor: account.avatarColor,
        avatarEmoji: account.avatarEmoji,
        accountId: account.id,
      };
      saveUser(derived);
      setMe(derived);
      return;
    }
    navigate("/");
  }, [me, account, authLoading, navigate]);

  // presence: notify friends of the party + one-tap join availability
  useEffect(() => {
    if (!account) return;
    enterRoom(code);
    return () => leaveRoom();
  }, [account, code, enterRoom, leaveRoom]);

  // open the Live TV panel automatically when started via the Home "Live TV" button
  useEffect(() => {
    if (searchParams.get("tv") === "1") setTvPanelOpen(true);
    const m3u = searchParams.get("m3u");
    if (m3u) {
      setAutoM3u(m3u);
      // strip the playlist param so a refresh / shared link doesn't re-parse it
      try { window.history.replaceState(null, "", window.location.pathname); } catch (e) { console.debug("clean m3u param failed", e); }
    }
    // auto-open the YouTube search when launched from a Home service tile
    if (searchParams.get("search") === "1") {
      const t = setTimeout(() => { try { openSearchRef.current && openSearchRef.current(); } catch (e) { console.debug("auto search open failed", e); } }, 900);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = useCallback((obj) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(obj));
    }
  }, []);

  const maybeSendRead = useCallback(() => {
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    const visible = isDesktop ? (document.hasFocus() && chatAtBottomRef.current) : mobileChatOpenRef.current;
    if (visible) send({ type: "read", at: new Date().toISOString() });
  }, [send]);

  const handleMessage = useCallback((ev) => {
    const data = JSON.parse(ev.data);
    switch (data.type) {
      case "init":
        setMyId(data.you?.id || null);
        setViewers(data.viewers || []);
        setMessages(data.messages || []);
        if (data.personality) setPersonality(data.personality);
        setBrowseUrl(data.browseUrl || null);
        setIptvChannel(data.iptvChannel || null);
        setDrivers(data.drivers || []);
        setVoiceIds(new Set(data.voice || []));
        setUpNext(data.upNext || []);
        setPins(data.pins || []);
        setReads(data.reads || []);
        break;
      case "viewers":
        setViewers(data.viewers || []);
        break;
      case "personality":
        setPersonality(data.value);
        break;
      case "typing": {
        const nm = data.name;
        if (data.isTyping) {
          setTypingUsers((u) => (u.includes(nm) ? u : [...u, nm]));
          clearTimeout(typingTimers.current[nm]);
          typingTimers.current[nm] = setTimeout(() => {
            setTypingUsers((u) => u.filter((x) => x !== nm));
          }, 4000);
        } else {
          clearTimeout(typingTimers.current[nm]);
          setTypingUsers((u) => u.filter((x) => x !== nm));
        }
        break;
      }
      case "chat":
        setTypingUsers((u) => u.filter((x) => x !== data.message.name));
        setMessages((prev) => prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]);
        notifyNewMessage(data.message);
        maybeSendRead();
        break;
      case "read_state":
        setReads(data.reads || []);
        break;
      case "pins":
        setPins(data.pins || []);
        break;
      case "translation":
        setMessages((prev) => prev.map((m) => m.id === data.messageId ? { ...m, translations: { ...m.translations, ...data.translations } } : m));
        break;
      case "reaction": {
        registerReaction(data.emoji);
        break;
      }
      case "video_ack": {
        if (data.ok) toast.success(`✅ ${data.flag || ""} ${data.name} is watching`.trim());
        else toast.error(`⚠️ ${data.name} can't load this video`, { description: "The site may block embedding — try a different link or hit SYNC." });
        break;
      }
      case "msg_reaction":
        setMessages((prev) => prev.map((m) => m.id === data.messageId ? { ...m, reactions: data.reactions } : m));
        if (data.added && data.emoji) registerReaction(data.emoji);
        break;
      case "system": {
        const sid = "sys-" + Math.random().toString(36).slice(2);
        setMessages((prev) => [...prev, { id: sid, isSystem: true, text: data.text, event: data.event, flag: data.flag }]);
        if (data.event === "join" || data.event === "leave") {
          toast(`${data.flag || ""} ${data.text}`.trim());
          playChime(data.event);
        } else if (data.event === "codriver_removed") {
          toast(data.text);
        }
        break;
      }
      case "request_position": {
        const snap = playerRef.current?.snapshot?.();
        if (snap) send({ type: "report_position", forId: data.forId, currentTime: snap.currentTime, isPlaying: snap.isPlaying });
        break;
      }
      case "captions_available":
        clearCaptionCache(data.videoId);
        setCaptionSignal((n) => n + 1);
        toast.success("Captions updated for this video");
        break;
      case "captions_error":
        toast.error(data.message || "Couldn't load those captions");
        break;
      case "upnext":
        setUpNext(data.items || []);
        break;
      case "picking":
        setPickerActive(data.active ? data.name : null);
        break;
      case "sync_probe": {
        const snap = playerRef.current?.snapshot?.();
        if (snap) send({ type: "sync_report", forId: data.forId, currentTime: snap.currentTime, isPlaying: snap.isPlaying });
        break;
      }
      case "driver_time": {
        const mine = playerRef.current?.getTime?.();
        if (typeof mine === "number") {
          const diff = Math.abs(mine - (data.currentTime || 0));
          setSyncStatus(diff <= 1.75 ? "synced" : "behind");
        }
        break;
      }
      case "video":
        // playback sync for the browse-mode YouTube player (driver-controlled)
        if (data.action !== "load") {
          console.log(`[sync←] received "${data.action}" @ ${Math.round(data.currentTime || 0)}s${data.by ? ` from ${data.by}` : ""}`);
          playerRef.current?.applyRemote({ action: data.action, currentTime: data.currentTime });
        }
        break;
      case "browse":
        console.log(`[sync←] video URL ${data.url ? "received → " + data.url : "cleared by driver"}`);
        setBrowseUrl(data.url);
        if (data.url) setIptvChannel(null);
        if (data.drivers !== undefined) setDrivers(data.drivers);
        break;
      case "iptv_channel":
        setIptvChannel(data.channel || null);
        if (data.drivers !== undefined) setDrivers(data.drivers);
        if (data.channel) setBrowseUrl(null);
        break;
      case "driver":
        setDrivers(data.drivers || []);
        break;
      case "codriver_added":
        toast.success("You're now a co-driver! 🎬");
        break;
      case "you_are_driver":
        toast.success("You're now the driver! 🎬");
        break;
      case "control_request":
        setControlRequest(data);
        break;
      case "voice_peers":
        voiceApiRef.current.onVoicePeers?.(data.peers || []);
        break;
      case "voice_join":
        setVoiceIds((s) => new Set(s).add(data.id));
        break;
      case "voice_leave":
        setVoiceIds((s) => { const n = new Set(s); n.delete(data.id); return n; });
        voiceApiRef.current.peerLeave?.(data.id);
        break;
      case "signal":
        voiceApiRef.current.handleSignal?.(data.from, data.data);
        break;
      case "sync_countdown": {
        clearInterval(countdownTimerRef.current);
        setCountdown(3);
        playChime?.();
        let i = 3;
        countdownTimerRef.current = setInterval(() => {
          i -= 1;
          if (i > 0) { setCountdown(i); playChime?.(); }
          else if (i === 0) { setCountdown(0); playChime?.(); }
          else { clearInterval(countdownTimerRef.current); setCountdown(null); }
        }, 1000);
        break;
      }
      default:
        break;
    }
  }, [send, playChime, registerReaction, notifyNewMessage, maybeSendRead]);

  useEffect(() => {
    if (mobileChatOpen) maybeSendRead();
  }, [mobileChatOpen, maybeSendRead]);

  useEffect(() => {
    if (!me) return;
    unmountedRef.current = false;

    const scheduleReconnect = () => {
      if (unmountedRef.current) return;
      setReconnecting(true);
      attemptsRef.current += 1;
      const delay = Math.min(1000 * attemptsRef.current, 5000);
      clearTimeout(retryRef.current);
      retryRef.current = setTimeout(connect, delay);
    };

    const connect = () => {
      if (unmountedRef.current) return;
      let abandoned = false;
      let ws;
      try {
        ws = new WebSocket(wsUrl(code));
      } catch (e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;
      ws.__abandon = () => {
        abandoned = true;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.onopen = () => { try { ws.close(); } catch (e) { console.debug("stale ws close failed", e); } };
        if (ws.readyState === WebSocket.OPEN) { try { ws.close(); } catch (e) { console.debug("stale ws close failed", e); } }
      };

      ws.onopen = () => {
        if (abandoned) { try { ws.close(); } catch (e) { console.debug("abandoned ws close failed", e); } return; }
        attemptsRef.current = 0;
        setConnected(true);
        setReconnecting(false);
        ws.send(JSON.stringify({ type: "join", ...me }));
        if (rewatchUrlRef.current && !rewatchSentRef.current) {
          rewatchSentRef.current = true;
          ws.send(JSON.stringify({ type: "browse_nav", url: rewatchUrlRef.current }));
        }
      };
      ws.onmessage = handleMessage;
      ws.onclose = () => {
        if (abandoned) return;
        setConnected(false);
        scheduleReconnect();
      };
      ws.onerror = () => {
        if (abandoned) return;
        try { ws.close(); } catch (e) { console.debug("ws cleanup close failed", e); }
      };
    };

    connect();

    return () => {
      unmountedRef.current = true;
      clearTimeout(retryRef.current);
      const ws = wsRef.current;
      if (ws && ws.__abandon) ws.__abandon();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, me, handleMessage]);

  // Page Visibility: keep the socket alive when backgrounded, resync on return.
  useEffect(() => {
    const onVisibility = () => {
      const ws = wsRef.current;
      if (document.hidden) {
        // keepAlive — ping so the connection isn't reaped as idle while backgrounded
        if (ws && ws.readyState === WebSocket.OPEN) {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch (e) { /* ignore */ }
        }
      } else {
        // resync — rejoin to pull the latest room state; if we're the driver,
        // re-broadcast our exact position so everyone realigns
        if (ws && ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ type: "join", ...me }));
            if (isDriverRef.current) {
              const snap = playerRef.current?.snapshot?.();
              if (snap) ws.send(JSON.stringify({ type: "video", action: snap.isPlaying ? "play" : "seek", currentTime: snap.currentTime }));
            }
          } catch (e) { /* ignore */ }
        }
        // if the socket died while backgrounded, the onclose handler's
        // scheduleReconnect (with the "Reconnecting…" badge) brings it back
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // Auto true-fullscreen when the phone is turned sideways (best-effort; the video
  // already fills the screen on mobile, this hides the browser chrome where allowed).
  useEffect(() => {
    const mq = window.matchMedia("(orientation: landscape)");
    const isPhone = () =>
      window.matchMedia("(max-width: 1200px)").matches &&
      (window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0);
    const apply = () => {
      if (!isPhone()) return;
      if (mq.matches && hasMediaRef.current) requestFs(rootRef.current);
      else if (!mq.matches) exitFs();
    };
    const onChange = () => apply();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    window.addEventListener("orientationchange", onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
      window.removeEventListener("orientationchange", onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPlayerAction = useCallback((action, currentTime) => {
    // only the driver drives playback — prevents kickback/feedback loops
    if (!isDriverRef.current) return;
    // ignore the browser-forced pause when the driver backgrounds the tab / locks
    // the phone — otherwise everyone else's video would stop too
    if (action === "pause" && document.hidden) return;
    console.log(`[sync→] sent "${action}" @ ${Math.round(currentTime || 0)}s`);
    send({ type: "video", action, currentTime });
  }, [send]);

  const onSend = (text, replyTo) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "chat", text, replyTo }));
    } else {
      toast.error("⚠️ Message failed to send", { action: { label: "Retry", onClick: () => onSend(text, replyTo) } });
    }
  };
  const lastReactRef = useRef(0);
  const onReact = (emoji) => {
    const now = Date.now();
    if (now - lastReactRef.current < 250) return;  // light client cap; server also caps
    lastReactRef.current = now;
    send({ type: "reaction", emoji });
  };
  const onMsgReact = (messageId, emoji) => send({ type: "msg_react", messageId, emoji });
  const reportVideoStatus = useCallback((ok, reason) => send({ type: "video_ack", ok, reason }), [send]);

  const requestFs = (el) => {
    const req = el?.requestFullscreen || el?.webkitRequestFullscreen;
    if (!req || document.fullscreenElement || document.webkitFullscreenElement) return;
    try { const p = req.call(el); if (p && p.catch) p.catch(() => {}); } catch (e) { /* blocked */ }
  };
  const exitFs = () => {
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    if (!exit || !(document.fullscreenElement || document.webkitFullscreenElement)) return;
    try { const p = exit.call(document); if (p && p.catch) p.catch(() => {}); } catch (e) { /* ignore */ }
  };
  const toggleFullscreen = () => {
    if (document.fullscreenElement || document.webkitFullscreenElement) exitFs();
    else requestFs(rootRef.current);
  };

  const shareWhatsApp = () => {
    const link = `${window.location.origin}/room/${code}`;
    const text = `🎬 Join my Ibeflix watch party! Room code ${code} — ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const startSync = () => send({ type: "sync_countdown" });

  const selectChannel = (ch) => { send({ type: "iptv_channel", channel: ch }); setTvPanelOpen(false); };
  const exitIptv = () => send({ type: "iptv_channel", channel: null });
  const onTyping = useCallback((isTyping) => send({ type: "typing", isTyping }), [send]);

  const togglePip = async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        return;
      }
      const video = document.querySelector("video");
      if (video && document.pictureInPictureEnabled) {
        await video.requestPictureInPicture();
      } else {
        toast.info("Use the player's built-in PiP button — the browser blocks external PiP for embedded videos");
      }
    } catch (e) {
      toast.info("Picture-in-Picture isn't available for this video");
    }
  };
  const changePersonality = (value) => {
    if (value === personality) return;
    setPersonality(value);
    send({ type: "personality", value });
  };

  const isDriver = !!(myId && drivers.some((d) => d.id === myId));
  isDriverRef.current = isDriver;

  // passengers periodically check how aligned they are with the driver (YouTube only)
  useEffect(() => {
    if (isDriver || !browseUrl || !extractVideoId(browseUrl)) { setSyncStatus(null); return; }
    send({ type: "sync_query" });
    const id = setInterval(() => send({ type: "sync_query" }), 4000);
    return () => clearInterval(id);
  }, [isDriver, browseUrl, send]);

  const browseNav = (url) => send({ type: "browse_nav", url });
  const changeVideo = () => send({ type: "browse_clear" });
  const resync = () => {
    if (!browseUrl) return;
    // re-send the URL AND the driver's exact current position so everyone realigns
    send({ type: "browse_nav", url: browseUrl });
    const snap = playerRef.current?.snapshot?.();
    if (snap && typeof snap.currentTime === "number") {
      console.log(`[sync→] RESYNC ALL → ${snap.isPlaying ? "play" : "pause"} @ ${Math.round(snap.currentTime)}s`);
      send({ type: "video", action: snap.isPlaying ? "play" : "seek", currentTime: snap.currentTime });
    }
    toast.success("Resynced everyone to your spot");
  };
  const inviteCoDriver = (clientId) => send({ type: "set_driver", clientId });
  const pinMessage = (msg) => send({ type: "pin", message: { id: msg.id, name: msg.name, text: msg.text } });
  const unpinMessage = (id) => send({ type: "unpin", messageId: id });
  const removeDriver = (clientId) => send({ type: "remove_driver", clientId });
  const stepDown = () => send({ type: "remove_driver", clientId: myId });
  const onUploadCaptions = (content, format, videoId) => send({ type: "upload_captions", content, format, videoId });
  const onSeekTo = (seconds) => {
    playerRef.current?.applyRemote?.({ action: "seek", currentTime: seconds });
    send({ type: "video", action: "seek", currentTime: seconds });
  };
  const onRecap = () => { send({ type: "recap" }); toast("Cooking up a party recap… 🍿"); };
  const onAddNext = (url) => send({ type: "upnext_add", url });
  const onRemoveNext = (id) => send({ type: "upnext_remove", id });
  const onPlayNext = (id) => send({ type: "upnext_play", id });
  const onPickingChange = (active) => send({ type: "picking", active });
  const requestControl = () => {
    send({ type: "request_control" });
    toast.info("Asked the drivers to add you");
  };

  useEffect(() => {
    if (!controlRequest) return;
    if (isDriver && controlRequest.id !== myId && drivers.length < 2) {
      toast(`${controlRequest.flag} ${controlRequest.name} wants to co-drive`, {
        action: { label: "Add co-driver", onClick: () => inviteCoDriver(controlRequest.id) },
      });
    }
    setControlRequest(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlRequest, isDriver, myId]);

  const copyCode = () => {
    try {
      navigator.clipboard?.writeText(code).catch(() => {});
    } catch (e) { /* clipboard blocked */ }
    setCopied(true);
    toast.success("Room code copied");
    setTimeout(() => setCopied(false), 1600);
  };

  // ---------------- Voice chat (WebRTC mesh over WS signaling) ----------------
  const createPeer = (peerId, initiator) => {
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    peersRef.current[peerId] = pc;
    const ls = localStreamRef.current;
    if (ls) ls.getTracks().forEach((t) => pc.addTrack(t, ls));
    pc.onicecandidate = (e) => {
      if (e.candidate) send({ type: "signal", to: peerId, data: { kind: "ice", candidate: e.candidate } });
    };
    pc.ontrack = (e) => {
      const stream = e.streams[0];
      setRemoteStreams((prev) => ({ ...prev, [peerId]: stream }));
    };
    if (initiator) {
      (async () => {
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          send({ type: "signal", to: peerId, data: { kind: "offer", sdp: offer } });
        } catch (e) { console.debug("voice offer failed", e); }
      })();
    }
    return pc;
  };

  const peerLeave = (peerId) => {
    const pc = peersRef.current[peerId];
    if (pc) { try { pc.close(); } catch (e) { console.debug("peer close failed", e); } delete peersRef.current[peerId]; }
    setRemoteStreams((prev) => { const n = { ...prev }; delete n[peerId]; return n; });
  };

  const onVoicePeers = (peers) => { peers.forEach((p) => createPeer(p.id, true)); };

  const handleSignal = async (from, d) => {
    if (!localStreamRef.current || !d) return;
    let pc = peersRef.current[from];
    try {
      if (d.kind === "offer") {
        if (!pc) pc = createPeer(from, false);
        await pc.setRemoteDescription(new RTCSessionDescription(d.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        send({ type: "signal", to: from, data: { kind: "answer", sdp: answer } });
      } else if (d.kind === "answer") {
        if (pc) await pc.setRemoteDescription(new RTCSessionDescription(d.sdp));
      } else if (d.kind === "ice") {
        if (pc && d.candidate) { try { await pc.addIceCandidate(new RTCIceCandidate(d.candidate)); } catch (e) { console.debug("ICE add failed", e); } }
      }
    } catch (e) { console.debug("voice signal failed", e); }
  };

  voiceApiRef.current = { onVoicePeers, handleSignal, peerLeave };

  const joinVoice = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      setInVoice(true);
      setMuted(false);
      if (myId) setVoiceIds((s) => new Set(s).add(myId));
      send({ type: "voice_join" });
    } catch (e) {
      toast.error("Microphone permission is needed for voice chat");
    }
  };

  const leaveVoice = (silent) => {
    if (!silent) send({ type: "voice_leave" });
    Object.values(peersRef.current).forEach((pc) => { try { pc.close(); } catch (e) { console.debug("peer close failed", e); } });
    peersRef.current = {};
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    setRemoteStreams({});
    setInVoice(false);
    if (myId) setVoiceIds((s) => { const n = new Set(s); n.delete(myId); return n; });
  };

  const toggleMute = () => {
    const s = localStreamRef.current;
    if (!s) return;
    const track = s.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setMuted(!track.enabled); }
  };

  useEffect(() => () => {
    Object.values(peersRef.current).forEach((pc) => { try { pc.close(); } catch (e) { console.debug("peer close failed", e); } });
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  if (!me) return null;

  const isYouTube = !!browseUrl && !!extractVideoId(browseUrl);
  const immersive = isYouTube;
  const barsShown = immersive ? barsVisible : true;
  hasMediaRef.current = !!browseUrl || !!iptvChannel;

  return (
    <div ref={rootRef} className="flex flex-col md:grid md:grid-cols-[minmax(0,6fr)_minmax(0,4fr)] lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)] h-[100dvh] w-full bg-[#060817] overflow-hidden">
      {/* Left: video stage */}
      <div className="flex flex-col min-h-0 min-w-0 relative max-md:flex-1">
        {/* Mobile compact header (≤44px) — overlays video, auto-hides with toolbar */}
        <div
          data-testid="mobile-header"
          className={`md:hidden absolute top-0 inset-x-0 z-40 h-11 px-3 flex items-center justify-between bg-gradient-to-b from-black/85 via-black/45 to-transparent transition-opacity duration-300 ${barsShown ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <BrandLogo className="text-lg" />
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2" data-testid="mobile-viewers">
              {viewers.slice(0, 4).map((v) => (
                <div key={v.id} className="w-7 h-7 rounded-full bg-[#0F1636] border border-white/20 flex items-center justify-center text-sm" title={v.name}>{v.flag}</div>
              ))}
              {viewers.length > 4 && (
                <div className="w-7 h-7 rounded-full bg-[#0F1636] border border-white/20 flex items-center justify-center text-[10px] font-bold text-[#A0A0AB]">+{viewers.length - 4}</div>
              )}
            </div>
            <button
              data-testid="mobile-leave-button"
              onClick={() => navigate("/")}
              title="Leave"
              className="w-8 h-8 shrink-0 rounded-full bg-white/10 text-[#E4E4E7] hover:text-[#FF3B5C] flex items-center justify-center active:scale-90 transition-all"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* top bar (desktop) */}
        <div className="hidden md:flex items-center justify-between px-5 py-3 border-b border-white/5 bg-[#0A0E27] shrink-0">
          <div className="flex items-center gap-4">
            <BrandLogo className="text-2xl" />
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-xs text-[#71717A] uppercase tracking-wider">Room</span>
              <button
                data-testid="copy-code-button"
                onClick={copyCode}
                className="flex items-center gap-2 bg-[#0F1636] border border-white/10 rounded-full px-3 py-1.5 hover:border-[#00D9FF] transition-colors group"
              >
                <span className="font-display font-bold tracking-[0.25em] text-[#F1F1F5]">{code}</span>
                {copied ? <Check className="w-3.5 h-3.5 text-[#00FF66]" /> : <Copy className="w-3.5 h-3.5 text-[#71717A] group-hover:text-[#00D9FF]" />}
              </button>
              <button
                data-testid="whatsapp-share-button"
                onClick={shareWhatsApp}
                title="Invite friends on WhatsApp"
                className="flex items-center gap-1.5 bg-[#0F1636] border border-white/10 rounded-full px-3 py-1.5 hover:border-[#25D366] text-[#A0A0AB] hover:text-[#25D366] transition-colors text-xs font-semibold"
              >
                <Share2 className="w-3.5 h-3.5" /><span className="hidden lg:inline">Invite</span>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {inVoice ? (
              <div data-testid="voice-controls" className="flex items-center gap-1 bg-[#0F1636] border border-[#00D9FF]/40 rounded-full p-1">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-[#00D9FF] px-2">
                  <Headphones className="w-3.5 h-3.5" /> {voiceIds.size}
                </span>
                <button
                  data-testid="voice-mute-button"
                  onClick={toggleMute}
                  title={muted ? "Unmute" : "Mute"}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${muted ? "bg-white/5 text-[#88A0C0]" : "bg-[#00D9FF] text-navy-ink"}`}
                >
                  {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
                <button
                  data-testid="voice-leave-button"
                  onClick={() => leaveVoice(false)}
                  title="Leave voice"
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 text-[#A0A0AB] hover:bg-white/10 hover:text-white transition-colors"
                >
                  <PhoneOff className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                data-testid="voice-join-button"
                onClick={joinVoice}
                title="Join voice chat"
                className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-[#0F1636] border border-white/10 text-[#A0A0AB] hover:text-white hover:border-[#00D9FF] transition-colors"
              >
                <Mic className="w-3.5 h-3.5" /><span className="hidden md:inline">Voice</span>
              </button>
            )}
            <PersonalityPicker value={personality} onChange={changePersonality} />
            <button
              data-testid="pip-button"
              onClick={togglePip}
              title="Picture-in-Picture"
              className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-[#0F1636] border border-white/10 text-[#A0A0AB] hover:text-white hover:border-[#00D9FF] transition-colors"
            >
              <PictureInPicture2 className="w-4 h-4" />
            </button>
            <button
              data-testid="livetv-open-button"
              onClick={() => setTvPanelOpen(true)}
              title="Watch live TV together (paste an M3U playlist)"
              className="flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#0F1636] border border-white/10 text-[#A0A0AB] hover:text-white hover:border-[#00D9FF] transition-colors text-xs font-semibold"
            >
              📡<span className="hidden md:inline">Live TV</span>
            </button>
            <button
              data-testid="sync-countdown-button"
              onClick={startSync}
              title="Countdown & play together — 3…2…1…PLAY"
              className="btn-neon flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#00D9FF] text-navy-ink hover:bg-[#33E4FF] hover:-translate-y-[1px] active:scale-95 text-xs font-display font-bold uppercase tracking-wider shadow-[0_0_18px_rgba(0,217,255,0.6)]"
            >
              <Clapperboard className="w-3.5 h-3.5" /> SYNC
            </button>
            <button
              data-testid="recap-button"
              onClick={onRecap}
              title="Ask the AI host for a party recap"
              className="flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#0F1636] border border-white/10 text-[#A0A0AB] hover:text-white hover:border-[#00D9FF] transition-colors text-xs font-semibold"
            >
              <Sparkles className="w-3.5 h-3.5" /><span className="hidden md:inline">Recap</span>
            </button>
            <div className="relative hidden sm:block">
              <button
                data-testid="notif-settings-button"
                onClick={() => setNotifOpen((v) => !v)}
                title="Message notifications"
                className="flex items-center justify-center w-9 h-9 rounded-full bg-[#0F1636] border border-white/10 text-[#A0A0AB] hover:text-white hover:border-[#00D9FF] transition-colors"
              >
                <Bell className="w-4 h-4" />
              </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-[80]" onClick={() => setNotifOpen(false)} />
                  <div data-testid="notif-settings-popover" className="absolute right-0 mt-2 w-60 z-[81] bg-[#0F1636] border border-white/10 rounded-2xl p-3 shadow-2xl">
                    <p className="text-xs font-bold text-[#F1F1F5] uppercase tracking-wider mb-1 px-1">Message notifications</p>
                    <NotifToggle testId="notif-sound-toggle" label="Sound" checked={notifPrefs.sound} onChange={(v) => setPref("sound", v)} />
                    <NotifToggle testId="notif-visual-toggle" label="Pop-up banner" checked={notifPrefs.visual} onChange={(v) => setPref("visual", v)} />
                    <NotifToggle testId="notif-badge-toggle" label="Unread badge" checked={notifPrefs.badge} onChange={(v) => setPref("badge", v)} />
                  </div>
                </>
              )}
            </div>
            <span className={`hidden sm:flex items-center gap-1.5 text-xs ${connected ? "text-[#00FF66]" : reconnecting ? "text-[#E6A429]" : "text-[#71717A]"}`}>
              <span className={`w-2 h-2 rounded-full ${connected ? "bg-[#00FF66]" : reconnecting ? "bg-[#E6A429] animate-pulse" : "bg-[#71717A]"}`} />
              {connected ? "Live" : reconnecting ? "Reconnecting…" : "Connecting…"}
            </span>
            <button
              data-testid="leave-room-button"
              onClick={() => navigate("/")}
              className="btn-neon flex items-center gap-2 text-sm text-[#88A0C0] hover:text-[#FF3B5C] bg-white/5 border border-transparent hover:border-[#FF0040]/50 hover:shadow-[0_0_18px_rgba(255,0,64,0.4)] transition-all rounded-full px-4 py-2"
            >
              <LogOut className="w-4 h-4" /> Leave
            </button>
          </div>
        </div>

        {/* stage — Browse Together video OR Live TV */}
        <div className="relative flex-1 min-h-0">
          {iptvChannel ? (
            <IPTVPlayer
              channel={iptvChannel}
              isDriver={isDriver || drivers.length === 0}
              onOpenPanel={() => setTvPanelOpen(true)}
              onExit={exitIptv}
            />
          ) : (
          <BrowseTogether
            browseUrl={browseUrl}
            isDriver={isDriver}
            drivers={drivers}
            viewers={viewers}
            myId={myId}
            onInviteCoDriver={inviteCoDriver}
            onRemoveDriver={removeDriver}
            onStepDown={stepDown}
            onNavigate={browseNav}
            onBack={changeVideo}
            onRequestControl={requestControl}
            onResync={resync}
            playerRef={playerRef}
            onAction={onPlayerAction}
            onVideoStatus={reportVideoStatus}
            viewerLanguage={me.language}
            onUploadCaptions={onUploadCaptions}
            onSeek={onSeekTo}
            captionSignal={captionSignal}
            syncStatus={syncStatus}
            upNext={upNext}
            onAddNext={onAddNext}
            onRemoveNext={onRemoveNext}
            onPlayNext={onPlayNext}
            region={me.countryCode || "US"}
            onPickingChange={onPickingChange}
            pickerActive={pickerActive}
            openSearchRef={openSearchRef}
          />
          )}
          {/* Mobile: tap the video to show/hide controls (YouTube immersive) */}
          {immersive && (
            <button
              type="button"
              data-testid="mobile-tap-zone"
              aria-label="Toggle controls"
              onClick={() => setBarsVisible((v) => !v)}
              className="md:hidden absolute inset-x-0 top-0 bottom-[84px] z-10"
            />
          )}

          {/* Mobile floating toolbar */}
          <div
            data-testid="mobile-toolbar"
            className={`md:hidden absolute bottom-3 inset-x-3 z-40 flex items-center gap-2 transition-all duration-300 ${barsShown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"}`}
          >
            <button
              data-testid="mobile-room-code"
              onClick={copyCode}
              className="flex items-center gap-2 h-11 px-3.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 active:scale-95 transition-transform"
            >
              <span className="font-display font-bold tracking-[0.2em] text-sm text-[#F1F1F5]">{code}</span>
              {copied ? <Check className="w-3.5 h-3.5 text-[#00FF66]" /> : <Copy className="w-3.5 h-3.5 text-[#71717A]" />}
            </button>
            <div data-testid="mobile-viewer-count" className="flex items-center gap-1.5 h-11 px-3 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-[#F1F1F5]">
              <Users className="w-4 h-4 text-[#00D9FF]" />
              <span className="text-sm font-semibold">{viewers.length}</span>
            </div>
            <div className="flex-1" />
            <button
              data-testid="mobile-chat-button"
              onClick={() => { setMobileChatOpen(true); setUnread(0); }}
              title="Chat"
              className="relative w-11 h-11 shrink-0 rounded-full bg-[#FF006E] flex items-center justify-center text-white active:scale-90 transition-transform shadow-[0_0_18px_rgba(255,0,110,0.6)]"
            >
              <MessageCircle className="w-5 h-5" />
              {notifPrefs.badge && unread > 0 && (
                <span data-testid="mobile-chat-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-white text-[#FF006E] text-[10px] font-bold flex items-center justify-center border-2 border-[#FF006E]">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-more-button"
              onClick={() => setMoreOpen(true)}
              title="More"
              className="w-11 h-11 shrink-0 rounded-full bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center text-[#E4E4E7] active:scale-90 transition-transform"
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile emoji tray removed — reactions live in the chat panel only */}
        </div>
      </div>

      {/* Chat — desktop side panel / mobile slide-up drawer */}
      <div
        data-testid="chat-container"
        className={`relative min-h-0 min-w-0 md:relative md:h-full md:translate-y-0 md:z-auto md:rounded-none md:shadow-none
          max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-[60] max-md:h-[62%] max-md:rounded-t-2xl max-md:overflow-hidden max-md:shadow-[0_-8px_40px_rgba(0,0,0,0.6)] max-md:transition-transform max-md:duration-300 ${mobileChatOpen ? "max-md:translate-y-0" : "max-md:translate-y-full"}`}
      >
        {floats.length > 0 && (
          <div data-testid="chat-emoji-floats" className="absolute inset-0 z-30 pointer-events-none overflow-hidden">
            {floats.map((f) => (
              <span key={f.id} className="absolute bottom-16 text-3xl animate-float" style={{ left: `${f.left}%`, filter: "drop-shadow(0 0 10px rgba(0,217,255,0.7))" }}>
                {f.emoji}
              </span>
            ))}
          </div>
        )}
        <ChatPanel viewers={viewers} messages={messages} me={me} onSend={onSend} onReact={onReact} onMsgReact={onMsgReact} onTyping={onTyping} typingUsers={typingUsers} drivers={drivers} myId={myId} canManage={isDriver} onInviteCoDriver={inviteCoDriver} onRemoveDriver={removeDriver} voiceIds={voiceIds} reads={reads} pins={pins} onPin={pinMessage} onUnpin={unpinMessage} onReport={(msg) => { setReportPrefill(msg?.text || ""); setReportOpen(true); }} scrollToBottomRef={chatScrollToBottomRef} onAtBottomChange={(v) => { chatAtBottomRef.current = v; if (v) maybeSendRead(); }} />
        {mobileChatOpen && (
          <button
            data-testid="mobile-chat-close"
            onClick={() => setMobileChatOpen(false)}
            className="md:hidden absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white active:scale-90 transition-transform"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {/* Mobile chat backdrop — tap outside to close */}
      {mobileChatOpen && (
        <div data-testid="mobile-chat-backdrop" onClick={() => setMobileChatOpen(false)} className="md:hidden fixed inset-0 z-[55] bg-black/40" />
      )}

      {/* Remote voice audio (hidden) */}
      <div style={{ display: "none" }} data-testid="voice-audio-sinks">
        {Object.entries(remoteStreams).map(([id, stream]) => (
          <audio key={id} autoPlay ref={(el) => { if (el && el.srcObject !== stream) el.srcObject = stream; }} />
        ))}
      </div>

      {/* Live TV channel picker */}
      <LiveTVPanel open={tvPanelOpen} onClose={() => setTvPanelOpen(false)} onSelect={selectChannel} autoLoadUrl={autoM3u} />

      {/* Report abuse (piracy links, harassment, etc.) */}
      <ReportAbuseModal open={reportOpen} language={me.language} prefill={reportPrefill} roomCode={code} onClose={() => setReportOpen(false)} />

      {/* Mobile "more" bottom sheet — secondary room controls */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-[70]" data-testid="mobile-more-sheet">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMoreOpen(false)} />
          <div className="absolute bottom-0 inset-x-0 bg-[#0A0E27] border-t border-white/10 rounded-t-2xl p-4 pb-8 animate-in slide-in-from-bottom duration-300">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-[#F1F1F5]">Room controls</span>
              <button data-testid="mobile-more-close" onClick={() => setMoreOpen(false)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[#A0A0AB] active:scale-90 transition-transform">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2.5">
              {(isDriver || drivers.length === 0) && (
                <Tile testId="mobile-more-search" onClick={() => { openSearchRef.current?.(); setMoreOpen(false); }} icon={<Search className="w-4 h-4" />} label="Search" />
              )}
              {browseUrl && (isDriver || drivers.length === 0) && (
                <Tile testId="mobile-more-change" onClick={() => { changeVideo(); setMoreOpen(false); }} icon={<ArrowLeft className="w-4 h-4" />} label="Change video" />
              )}
              <Tile testId="mobile-more-livetv" onClick={() => { setTvPanelOpen(true); setMoreOpen(false); }} icon={<Tv className="w-4 h-4" />} label="Live TV" />
              <Tile testId="mobile-more-sync" onClick={() => { startSync(); setMoreOpen(false); }} icon={<Clapperboard className="w-4 h-4" />} label="Sync" />
              <Tile testId="mobile-more-recap" onClick={() => { onRecap(); setMoreOpen(false); }} icon={<Sparkles className="w-4 h-4" />} label="Recap" />
              <Tile testId="mobile-more-pip" onClick={() => { togglePip(); setMoreOpen(false); }} icon={<PictureInPicture2 className="w-4 h-4" />} label="PiP" />
              <Tile testId="mobile-more-fullscreen" onClick={() => { toggleFullscreen(); setMoreOpen(false); }} icon={<Maximize className="w-4 h-4" />} label="Fullscreen" />
              <Tile testId="mobile-more-invite" onClick={() => { shareWhatsApp(); setMoreOpen(false); }} icon={<Share2 className="w-4 h-4" />} label="Invite" />
              <Tile testId="mobile-more-report" onClick={() => { setReportPrefill(""); setReportOpen(true); setMoreOpen(false); }} icon={<Flag className="w-4 h-4" />} label="Report" />
              {inVoice ? (
                <>
                  <Tile testId="mobile-more-mute" onClick={toggleMute} active={!muted} icon={muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />} label={muted ? "Unmute" : "Mute"} />
                  <Tile testId="mobile-more-leave-voice" onClick={() => leaveVoice(false)} icon={<PhoneOff className="w-4 h-4" />} label="End voice" />
                </>
              ) : (
                <Tile testId="mobile-more-voice" onClick={() => { joinVoice(); setMoreOpen(false); }} icon={<Mic className="w-4 h-4" />} label="Voice" />
              )}
            </div>
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="text-xs text-[#71717A] font-semibold uppercase tracking-wider">AI Host mood</span>
              <PersonalityPicker value={personality} onChange={changePersonality} />
            </div>
            <div className="mt-4 pt-4 border-t border-white/10">
              <span className="text-xs text-[#71717A] font-semibold uppercase tracking-wider">Message notifications</span>
              <div className="mt-1">
                <NotifToggle testId="mobile-notif-sound" label="Sound" checked={notifPrefs.sound} onChange={(v) => setPref("sound", v)} />
                <NotifToggle testId="mobile-notif-visual" label="Pop-up banner" checked={notifPrefs.visual} onChange={(v) => setPref("visual", v)} />
                <NotifToggle testId="mobile-notif-badge" label="Unread badge" checked={notifPrefs.badge} onChange={(v) => setPref("badge", v)} />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-center gap-1.5 text-xs">
              <span className={`w-2 h-2 rounded-full ${connected ? "bg-[#00FF66]" : reconnecting ? "bg-[#E6A429] animate-pulse" : "bg-[#71717A]"}`} />
              <span className={connected ? "text-[#00FF66]" : reconnecting ? "text-[#E6A429]" : "text-[#71717A]"}>
                {connected ? "Live — everyone's in sync" : reconnecting ? "Reconnecting…" : "Connecting…"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Sync countdown overlay — 3…2…1…PLAY for everyone */}
      {countdown !== null && (
        <div data-testid="sync-countdown-overlay" className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 backdrop-blur-sm pointer-events-none px-6">
          {countdown > 0 ? (
            <span key={countdown} className="text-white font-black leading-none text-[45vw] sm:text-[260px] animate-in zoom-in-50 duration-300 drop-shadow-[0_0_40px_rgba(0,217,255,0.85)]">
              {countdown}
            </span>
          ) : (
            <span data-testid="sync-play-now" className="text-white font-black text-center text-4xl sm:text-7xl animate-in zoom-in-75 duration-300 drop-shadow-[0_0_40px_rgba(0,217,255,0.85)]">
              ▶ PLAY NOW! 🎬
            </span>
          )}
        </div>
      )}
    </div>
  );
}
